import React, { useCallback, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons } from '../components/icons';
import { LigneAction } from '../components/LigneAction';
import { useGabarit } from '../hooks/useGabarit';
import { ScreenHeader } from '../components/ScreenHeader';
import { countCoverLetters, getLastCoverLetterAnalysis, listCoverLetters } from '../services/coverLetter';
import { formatSavedDate } from '../utils/cvDates';
import { colors, formes, gradients, layout, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import { encrer, FEATURE_ACCENTS } from '../theme/featureAccents';
import { t } from '../i18n';
import { PROPS_LTR, styleMiroir } from '../i18n/sens';
import type { SavedCoverLetter } from '../types';

export interface MyCoverLetterScreenProps {
  onBack?: () => void;
  onOpenCreate: () => void;
  onOpenList: () => void;
  onOpenAnalyse: () => void;
  // Rouvre une lettre depuis la grille. Absent : la vignette mène à la liste complète.
  onOpenLetter?: (letter: SavedCoverLetter) => void;
}

// Ce que la lettre apporte, en quatre points. Contenu FIXE : c'est de la pédagogie, pas un
// résultat — rien ici ne dépend d'un appel IA, donc rien ne peut être vide ni en erreur.
// Construit AU RENDU : constante de module = libellés figés à l'import.
const argumentsList = (): string[] => [
  t('myLetter.arguments.analysed'),
  t('myLetter.arguments.skills'),
  t('myLetter.arguments.structure'),
  t('myLetter.arguments.ready'),
];

// Libellé de la carte « Mes lettres ». Jamais « 0 lettre » : un compteur à zéro se dit en toutes
// lettres, il n'énonce pas un décompte.
export function lettersLabel(count: number): string {
  // Pluriel délégué à i18n-js : le français accorde le nom ET le participe, l'anglais ni l'un ni
  // l'autre — une concaténation ternaire ne peut pas porter les deux règles.
  return t('myLetter.savedCount', { count });
}

// Nombre de vignettes de lettres montrées sur le hub : deux, plus la case « Nouvelle ».
const VIGNETTES = 2;

// Rose en TEXTE sur le fond gris de l'écran ou sur sa propre teinte claire : `ink` n'est calculé que
// pour le blanc (4,6:1) et tombe à 3,9 sur la teinte. Plus sombre, il tient 4,5 sur les deux.
const ENCRE_LETTRE = encrer(FEATURE_ACCENTS.letter.color, 5.6);

/**
 * Entité « Ma lettre de motivation » : création, analyse et dernières lettres enregistrées.
 *
 * FAÇADE de navigation, même parti pris que « Mon CV » : chaque action pousse un écran existant,
 * l'écran ne porte aucun état non enregistré et n'a donc pas besoin de garde de sortie.
 *
 * « Améliorer ma lettre » (optimisation) n'existe pas côté serveur : l'entrée est ABSENTE, pas
 * grisée. Une entrée désactivée annoncerait une destination qui n'existe pas (lot 9c).
 */
export function MyCoverLetterScreen({
  onBack,
  onOpenCreate,
  onOpenList,
  onOpenAnalyse,
  onOpenLetter,
}: MyCoverLetterScreenProps) {
  const [count, setCount] = useState(0);
  const [score, setScore] = useState<number | null>(null);
  const [lettres, setLettres] = useState<SavedCoverLetter[]>([]);

  // Rechargement au retour d'écran : une lettre générée doit se voir dans le compteur sans quitter
  // l'onglet. Une lecture en échec retombe sur 0 — l'écran reste utilisable et toutes ses actions
  // restent accessibles, comme pour « Mon CV ».
  useFocusEffect(
    useCallback(() => {
      let active = true;
      countCoverLetters()
        .then((n) => active && setCount(n))
        .catch(() => active && setCount(0));
      // Échec silencieux : sans score, la pastille ne s'affiche pas et l'écran reste entier.
      getLastCoverLetterAnalysis()
        .then((a) => active && setScore(a?.diagnostic.score ?? null))
        .catch(() => active && setScore(null));
      // Échec silencieux aussi : sans vignettes, il reste « Nouvelle » et « Tout voir ».
      listCoverLetters(VIGNETTES)
        .then((l) => active && setLettres(l.slice(0, VIGNETTES)))
        .catch(() => active && setLettres([]));
      return () => {
        active = false;
      };
    }, []),
  );

  const ouvrir = (lettre: SavedCoverLetter) => (onOpenLetter ? onOpenLetter(lettre) : onOpenList());

  const { large } = useGabarit();
  const sPitch = (
    <>
        <LinearGradient
          testID="myletter-pitch"
          colors={[...gradients.nuitLettre]}
          start={{ x: 0.2, y: 0 }}
          end={{ x: 0.8, y: 1 }}
          style={[styles.pitchCard, large && styles.pitchCardL]}
        >
          <View style={[styles.pitchTexts, large && styles.pitchTextsL]}>
            <Text style={[styles.pitchLead, large && styles.pitchLeadL]}>{t('myLetter.pitchLead')}</Text>
            <View style={styles.checks}>
              {argumentsList().map((item) => (
                <View key={item} style={styles.check}>
                  <View style={styles.checkDot}>
                    <MaterialCommunityIcons name="check" size={12} color={colors.white} />
                  </View>
                  <Text style={[styles.checkText, large && styles.checkTextL]}>{item}</Text>
                </View>
              ))}
            </View>
          </View>
          {/* La feuille de la maquette : décorative, tournée de 5°, jamais lue. */}
          <View
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            style={[styles.feuille, large && styles.feuilleL]}
            {...PROPS_LTR}
          >
            <View style={[styles.trait, { width: '38%', alignSelf: 'flex-end', backgroundColor: '#C9B4D8' }]} />
            <View style={[styles.trait, { width: '30%', alignSelf: 'flex-end', backgroundColor: '#C9B4D8' }]} />
            <View style={[styles.trait, styles.traitTitre]} />
            {['100%', '92%', '100%', '66%', '96%'].map((largeur, i) => (
              <View key={i} style={[styles.trait, { width: largeur as `${number}%`, backgroundColor: '#E4D3E0' }]} />
            ))}
            <View style={styles.feuilleCoche}>
              <MaterialCommunityIcons name="check" size={16} color={colors.white} />
            </View>
          </View>
        </LinearGradient>

            </>
  );
  const sCta = (
    <>
<Pressable
          testID="myletter-create"
          accessibilityRole="button"
          accessibilityLabel={`${t('myLetter.createTitle')}, ${t('myLetter.createSubtitle')}`}
          onPress={onOpenCreate}
          style={[styles.cta, large && styles.ctaL]}
        >
          {/* Rose assombri (`ink`) : le blanc du sous-titre n'y tient que sur cette teinte. */}
          <LinearGradient
            colors={[FEATURE_ACCENTS.letter.ink, colors.violetLogo]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={StyleSheet.absoluteFill}
          />
          <View style={styles.ctaIcone}>
            <MaterialCommunityIcons name="email-edit-outline" size={24} color={colors.white} />
          </View>
          <View style={styles.ctaTextes}>
            <Text style={styles.ctaTitre}>{t('myLetter.createTitle')}</Text>
            <Text style={styles.ctaSousTitre}>{t('myLetter.createSubtitle')}</Text>
          </View>
          <View style={[styles.ctaFleche, styleMiroir()]}>
            <Ionicons name="arrow-forward" size={18} color={FEATURE_ACCENTS.letter.ink} />
          </View>
        </Pressable>

            </>
  );
  const sAnalyse = (
    <>
<LigneAction
          testID="myletter-analyse"
          titre={t('myLetter.analyseTitle')}
          sousTitre={t('myLetter.analyseSubtitle')}
          onPress={onOpenAnalyse}
          icone={
            <View style={styles.analyseIcone}>
              <MaterialCommunityIcons name="clipboard-check-outline" size={22} color={FEATURE_ACCENTS.letter.ink} />
            </View>
          }
          // Le score de la DERNIÈRE analyse enregistrée : l'afficher ne relance rien et ne coûte aucun
          // quota, c'est tout l'intérêt de l'avoir persisté. Il se lit avec la ligne, pas à part.
          detailAccessible={score !== null ? `${t('myLetter.lastScore')} : ${score}` : undefined}
          accessoire={
            score !== null ? (
              <View testID="myletter-score" style={styles.pastilleScore}>
                <Text style={styles.pastilleScoreTexte}>{score}</Text>
              </View>
            ) : undefined
          }
        />

            </>
  );
  const sListe = (
    <>
<View style={styles.entete}>
          <Text style={styles.sectionTitle}>
            {count > 0 ? t('myLetter.listCount', { count }) : lettersLabel(0)}
          </Text>
          <Pressable
            testID="myletter-list"
            accessibilityRole="button"
            accessibilityLabel={`${lettersLabel(count)}, ${t('myLetter.seeAll')}`}
            onPress={onOpenList}
            hitSlop={{ top: 14, bottom: 14, left: 10, right: 10 }}
          >
            <Text style={styles.toutVoir}>{t('myLetter.seeAll')}</Text>
          </Pressable>
        </View>
        <View style={styles.grille}>
          {lettres.map((lettre) => (
            <Pressable
              key={lettre.id}
              testID={`myletter-item-${lettre.id}`}
              accessibilityRole="button"
              accessibilityLabel={`${lettre.title}, ${formatSavedDate(lettre.created_at)}`}
              onPress={() => ouvrir(lettre)}
              style={styles.vignette}
            >
              <View style={[styles.vignetteFeuille, large && styles.vignetteFeuilleL]} {...PROPS_LTR}>
                <View style={[styles.vignetteTrait, { width: '55%', backgroundColor: '#E0B5D2' }]} />
                <View style={[styles.vignetteTrait, { width: '100%' }]} />
                <View style={[styles.vignetteTrait, { width: '100%' }]} />
                <View style={[styles.vignetteTrait, { width: '70%' }]} />
              </View>
              <View style={styles.vignetteTextes}>
                <Text style={styles.vignetteTitre} numberOfLines={1}>
                  {lettre.title}
                </Text>
                <Text style={styles.vignetteDate} numberOfLines={1}>
                  {formatSavedDate(lettre.created_at)}
                </Text>
              </View>
            </Pressable>
          ))}
          <Pressable
            testID="myletter-new"
            accessibilityRole="button"
            accessibilityLabel={`${t('myLetter.newLetter')}, ${t('myLetter.createTitle')}`}
            onPress={onOpenCreate}
            style={styles.nouvelle}
          >
            <Ionicons name="add" size={22} color={ENCRE_LETTRE} />
            <Text style={styles.nouvelleTexte}>{t('myLetter.newLetter')}</Text>
          </Pressable>
        </View>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title={t('myLetter.title')} variante="carte" testID="myletter-back" />

              {large ? (
          <ScrollView testID="myletter-colonnes" contentContainerStyle={styles.colonnesLettre} showsVerticalScrollIndicator={false}>
            <View style={styles.colGaucheLettre}>{sPitch}</View>
            <View style={styles.colDroiteLettre}>
              {sCta}
              {sAnalyse}
              {sListe}
            </View>
          </ScrollView>
        ) : (
          <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
            {sPitch}
            {sCta}
            {sAnalyse}
            {sListe}
          </ScrollView>
        )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  colonnesL: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colGaucheL: { flex: 1, minWidth: 0, gap: spacing.lg },
  colDroiteL: { flex: 1, minWidth: 0, gap: spacing.md },
  piedL: { marginTop: spacing.sm },
  // Ordinateur : la carte pédagogique prend toute la hauteur à gauche, les actions s'empilent à droite.
  colonnesLettre: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colGaucheLettre: { flex: 1, minWidth: 0 },
  colDroiteLettre: { flex: 1, minWidth: 0, gap: spacing.lg, justifyContent: 'center' },
  pitchCardL: { flex: 1, padding: spacing.xxl, minHeight: 420, justifyContent: 'center' },
  pitchTextsL: { width: '54%', gap: spacing.lg },
  pitchLeadL: { fontSize: 28, lineHeight: 34 },
  checkTextL: { fontSize: typography.bodyMd.size, lineHeight: typography.bodyMd.lineHeight },
  feuilleL: { width: 190, height: 250, top: '18%', end: spacing.xxl, padding: spacing.lg, gap: 10 },
  ctaL: { minHeight: 96 },
  vignetteFeuilleL: { height: 96 },
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.md, paddingBottom: spacing.xxl },

  // ── Carte pédagogique ──
  pitchCard: {
    minHeight: 236,
    padding: spacing.lg,
    borderRadius: formes.rayon.lg,
    overflow: 'hidden',
  },
  // Largeur bornée : la feuille tient dans le tiers droit de la carte.
  pitchTexts: { width: '58%', gap: spacing.md },
  pitchLead: { fontSize: 17, lineHeight: 22, fontWeight: '800', color: colors.white },
  checks: { gap: spacing.sm },
  check: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  checkDot: {
    width: 18,
    height: 18,
    borderRadius: radius.pill,
    backgroundColor: FEATURE_ACCENTS.letter.color,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkText: { flex: 1, fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.white },
  feuille: {
    // Un document, jamais retourné en droite à gauche : seule sa POSITION (`end`) suit le sens.
    direction: 'ltr',
    position: 'absolute',
    end: spacing.lg,
    top: 38,
    width: 108,
    height: 142,
    padding: spacing.md,
    gap: 6,
    borderRadius: radius.sm,
    backgroundColor: colors.white,
    transform: [{ rotate: '5deg' }],
    shadowColor: '#000',
    shadowOpacity: 0.28,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 10 },
    elevation: 8,
  },
  trait: { height: 3, borderRadius: 2 },
  traitTitre: { width: '48%', height: 4, marginTop: 6, backgroundColor: FEATURE_ACCENTS.letter.color },
  feuilleCoche: {
    position: 'absolute',
    end: -8,
    bottom: -8,
    width: 30,
    height: 30,
    borderRadius: radius.pill,
    borderWidth: 3,
    borderColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: FEATURE_ACCENTS.letter.color,
  },

  // ── « Créer une lettre » ──
  cta: {
    minHeight: 74,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md + 2,
    paddingHorizontal: spacing.lg,
    borderRadius: formes.rayon.lg - 4,
    overflow: 'hidden',
  },
  ctaIcone: {
    width: 46,
    height: 46,
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  ctaTextes: { flex: 1, minWidth: 0, gap: 2 },
  ctaTitre: { fontSize: 17, fontWeight: '800', color: colors.white },
  ctaSousTitre: { fontSize: typography.small.size, color: colors.white },
  ctaFleche: {
    width: 34,
    height: 34,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
  },

  // ── Analyse ──
  analyseIcone: {
    width: 44,
    height: 44,
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: FEATURE_ACCENTS.letter.tint,
  },
  pastilleScore: {
    minWidth: 34,
    height: 26,
    paddingHorizontal: spacing.sm,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: FEATURE_ACCENTS.letter.tint,
  },
  pastilleScoreTexte: { fontSize: typography.small.size, fontWeight: '800', color: ENCRE_LETTRE },

  // ── Mes lettres ──
  entete: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginTop: spacing.sm },
  sectionTitle: {
    flexShrink: 1,
    fontSize: typography.caption.size,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
    textTransform: 'uppercase',
  },
  toutVoir: { fontSize: typography.small.size, fontWeight: '800', color: ENCRE_LETTRE },
  grille: { flexDirection: 'row', gap: spacing.md },
  vignette: {
    flex: 1,
    minWidth: 0,
    gap: spacing.sm,
    padding: spacing.sm,
    borderRadius: radius.lg + 2,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  vignetteFeuille: {
    direction: 'ltr',
    height: 66,
    gap: 5,
    paddingHorizontal: 10,
    paddingTop: 10,
    borderRadius: 11,
    backgroundColor: FEATURE_ACCENTS.letter.tint,
  },
  vignetteTrait: { height: 3, borderRadius: 2, backgroundColor: '#EBC9DE' },
  vignetteTextes: { gap: 1, paddingHorizontal: 2, paddingBottom: 2 },
  vignetteTitre: { fontSize: typography.small.size, fontWeight: '800', color: colors.night },
  vignetteDate: { fontSize: typography.caption.size, color: colors.grey500 },
  nouvelle: {
    flex: 1,
    minWidth: 0,
    minHeight: formes.hauteur.lg + 20,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs + 2,
    borderRadius: radius.lg + 2,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#E3B9D5',
  },
  nouvelleTexte: { fontSize: typography.small.size, fontWeight: '800', color: ENCRE_LETTRE },
});
